import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLang, useL } from "@/lib/i18n";
import { api, errDetail, fmtDate, mtlToday } from "@/lib/api";
import { StatusBadge } from "@/components/member/Lists";
import { F, Card, RedBtn, GhostBtn, Toggle } from "./ui";

function ManualBooking({ members, onDone }) {
  const L = useL();
  const { tErr } = useLang();
  const [f, setF] = useState({ user_id: "", date: mtlToday(1), start_time: "10:00", duration_hours: 2, deduct_hours: true, note: "" });
  const save = async () => {
    try { await api.post("/admin/bookings", { ...f, duration_hours: Number(f.duration_hours) }); toast.success(L("Réservation créée", "Booking created")); onDone(); }
    catch (e) { toast.error(tErr(errDetail(e))); }
  };
  return (
    <Card title={L("Réservation manuelle", "Manual booking")} testId="admin-manual-booking">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6 lg:items-end">
        <div className="lg:col-span-2"><p className="mb-1.5 text-xs text-slate-400">{L("Membre", "Member")}</p>
          <Select value={f.user_id} onValueChange={(v) => setF({ ...f, user_id: v })}>
            <SelectTrigger className="border-white/15 bg-black/40" data-testid="manual-booking-member-select"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} · {m.email}</SelectItem>)}</SelectContent>
          </Select></div>
        <F id="manual-booking-date" label="Date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
        <F id="manual-booking-time" label={L("Début (MTL)", "Start (MTL)")} type="time" step="1800" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} />
        <F id="manual-booking-duration" label={L("Durée (h)", "Duration (h)")} type="number" step="0.5" min="0.5" value={f.duration_hours} onChange={(e) => setF({ ...f, duration_hours: e.target.value })} />
        <RedBtn disabled={!f.user_id} onClick={save} data-testid="manual-booking-submit">{L("Réserver", "Book")}</RedBtn>
      </div>
      <div className="mt-3 max-w-xs"><Toggle id="manual-booking-deduct" label={L("Déduire les heures", "Deduct hours")} checked={f.deduct_hours} onChange={(v) => setF({ ...f, deduct_hours: v })} /></div>
    </Card>
  );
}

export function BookingTable({ items, onCancel, testId }) {
  const { lang } = useLang();
  const L = useL();
  if (!items.length) return <p className="text-sm text-slate-500">—</p>;
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="w-full min-w-[620px] text-sm">
        <tbody>
          {items.map((b) => (
            <tr key={b.id} className="border-b border-white/5" data-testid={`admin-booking-row-${b.id}`}>
              <td className="py-3 capitalize text-white">{fmtDate(b.local_date, lang)}</td>
              <td className="font-mono text-slate-300">{b.local_start}–{b.local_end}</td>
              <td className="text-slate-300">{b.kind === "block" ? b.reason : <>{b.user_name}<span className="block text-xs text-slate-500">{b.user_email}</span></>}</td>
              <td><StatusBadge s={b.status} /></td>
              <td className="text-right">{onCancel && b.status === "confirmed" && (
                <span className="flex justify-end gap-2">
                  {b.kind === "session" ? <>
                    <GhostBtn onClick={() => onCancel(b, true)} data-testid={`admin-cancel-refund-${b.id}`}>{L("Annuler + rembourser", "Cancel + refund")}</GhostBtn>
                    <GhostBtn onClick={() => onCancel(b, false)} data-testid={`admin-cancel-norefund-${b.id}`}>{L("Sans remb.", "No refund")}</GhostBtn>
                  </> : <GhostBtn onClick={() => onCancel(b, false)} data-testid={`admin-remove-block-${b.id}`}>{L("Retirer", "Remove")}</GhostBtn>}
                </span>)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminBookings() {
  const L = useL();
  const { lang, tErr } = useLang();
  const [scope, setScope] = useState("upcoming");
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [wl, setWl] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/bookings", { params: { scope } }).then((r) => setItems(r.data));
    api.get("/admin/waitlist").then((r) => setWl(r.data));
  }, [scope]);
  useEffect(() => { load(); api.get("/admin/members").then((r) => setMembers(r.data)); }, [load]);
  const cancel = async (b, refund) => {
    try { await api.post(`/admin/bookings/${b.id}/cancel`, { refund }); toast.success(L("Annulée", "Cancelled")); load(); }
    catch (e) { toast.error(tErr(errDetail(e))); }
  };
  return (
    <div className="space-y-6">
      <ManualBooking members={members} onDone={load} />
      <Card title={L("Réservations", "Reservations")} testId="admin-bookings"
        right={<div className="flex gap-2">{["upcoming", "all"].map((s) => <GhostBtn key={s} onClick={() => setScope(s)} data-testid={`admin-bookings-scope-${s}`} className={scope === s ? "border-red-500 text-white" : ""}>{s === "upcoming" ? L("À venir", "Upcoming") : L("Toutes", "All")}</GhostBtn>)}</div>}>
        <BookingTable items={items} onCancel={cancel} testId="admin-bookings-table" />
      </Card>
      <Card title={L("Liste d'attente", "Waitlist")} testId="admin-waitlist">
        {!wl.length ? <p className="text-sm text-slate-500">—</p> : (
          <ul className="space-y-1 text-sm">{wl.map((w) => <li key={w.id} className="flex justify-between border-b border-white/5 py-2 text-slate-300"><span><span className="capitalize">{fmtDate(w.local_date, lang)}</span> · {w.local_start}–{w.local_end} · {w.user_name}</span><StatusBadge s={w.status} /></li>)}</ul>
        )}
      </Card>
    </div>
  );
}
